import { Castle, Globe, LayoutDashboard, Map as MapIcon, MapPin, Trees, type LucideIcon } from 'lucide-react';

// ============================================================
// Mondes (NationsGlory) : onglets de la page
// ============================================================
// Source unique des clés, libellés, icônes et descriptions des onglets : la barre d'onglets
// (WorldsPage) et les cartes de l'accueil (HomeTab) en dérivent, pour ne jamais diverger.
// Les onglets naviguent par l'URL (?tab=<clé>) ; paramètres lus par les onglets :
//   ?tab=carte&pays=<countryId>      cadre la carte sur un pays (existant)
//   ?tab=carte&region=<geoRegionId>  couche Régions allumée, cadrage sur la région (lu par la carte)
//   ?tab=niveaux&nouveau=1           ouvre la création d'un niveau, puis retire le paramètre
//   ?tab=pays&filtre=<clé>           présélectionne un filtre de la liste (COUNTRY_FILTERS)
// ============================================================

export type WorldsTabKey = 'accueil' | 'carte' | 'pays' | 'regions' | 'niveaux' | 'marqueurs';

export interface WorldsTabMeta {
  key: WorldsTabKey;
  label: string;
  icon: LucideIcon;
  /** Phrase des cartes de l'accueil. */
  description: string;
}

export const WORLDS_TABS: readonly WorldsTabMeta[] = [
  {
    key: 'accueil', label: 'Accueil', icon: LayoutDashboard,
    description: 'Vue d\'ensemble : chiffres clés de la carte et points à surveiller.',
  },
  {
    key: 'carte', label: 'Carte', icon: MapIcon,
    description: 'Peindre les chunks par pays, placer les centres et voir la carte telle que le jeu la sert.',
  },
  {
    key: 'pays', label: 'Pays', icon: Castle,
    description: 'Créer et modifier les pays : noms, couleurs, drapeaux, centres et activation.',
  },
  {
    key: 'regions', label: 'Régions', icon: MapPin,
    description: 'Regrouper des pays en géo-régions, avec leurs stocks de pétrole et de gaz.',
  },
  {
    key: 'niveaux', label: 'Mondes (niveaux)', icon: Globe,
    description: 'Les mondes Minecraft déclarés et leur correspondance avec la carte et NG Java.',
  },
  {
    key: 'marqueurs', label: 'Marqueurs de biomes', icon: Trees,
    description: 'Placer les marqueurs de biomes sur la carte et gérer le catalogue des biomes.',
  },
];

/** Onglet ouvert à la première visite (ensuite : le dernier visité, mémorisé). */
export const DEFAULT_TAB: WorldsTabKey = 'accueil';

/** Clé localStorage du dernier onglet visité. */
export const TAB_STORAGE_KEY = 'worlds.tab';

export function isWorldsTab(v: string | null | undefined): v is WorldsTabKey {
  return !!v && WORLDS_TABS.some((t) => t.key === v);
}

export function tabMeta(key: WorldsTabKey): WorldsTabMeta {
  return WORLDS_TABS.find((t) => t.key === key) ?? WORLDS_TABS[0];
}

/** Navigation vers un onglet, avec des paramètres d'URL en plus (ex. { filtre: 'sans-centre' }). */
export type GoToTab = (tab: WorldsTabKey, extra?: Record<string, string>) => void;
